import LayoutBus from 'js/layouts/sidebar-menu/layout-bus.js'
import {openModalAlert, openModalConfirmRemove} from 'js/services/modals'
import {max, cloneDeep, pickBy} from 'lodash'
import httpRequestConfig from '../../nodes/http-request'
import splitConfig from '../../nodes/split'
import ctiConfig from '../../nodes/cti'
import sendSmsConfig from '../../nodes/sms'
import startConfig from '../../nodes/start-form'
import conditionConfig from '../../nodes/condition'
import updateConfig from '../../nodes/update'
import webhookConfig from '../../nodes/webhook'
import sendEmailConfig from '../../nodes/sendEmail'
import subWorkflowConfig from '../../nodes/sub-workflow'
import sendLeadConfig from '../../nodes/send-lead'
import pauseConfig from '../../nodes/pause'
import agentConfig from '../../nodes/agent'
import whatsappConfig from '../../nodes/whatsapp'
import waitForConfig from '../../nodes/waitFor'
import template from './_workflow-editor.pug'

const StepInternalKeys = ['classes', 'x', 'y', 'dropzone', 'errors', 'tags']

// Componente que gestiona el editor de workflow de un Automation
// Recibe el conjunto de nodos (steps) a representar, si no, inicia uno nuevo con un nodo start
//
// Utiliza una tabla para dibujar el diagrama (asegura distancia entre nodos y alineación)
// Esta tabla se compone de varios tipos de casillas: vacias, de nodos, de caminos entre nodos
//
// Al pulsar cada nodo aparece un tooltip con su información ampliada
// Desde el tooltip se puede acceder a su configuración (un modal con un formulario)
// También se puede eliminar el nodo si es el último de su rama (no tiene hijos)
//
// Si un nodo tiene errores se debe poner su borde en rojo
//
// Un nodo puede tener ninguno (end), uno o dos nodos hijos
// Al añadir un nuevo nodo, se dibujan las casillas de sus hijos con un borde naranja (dropzone)
// Los nodos se añaden arrastrándolos desde el panel de la izquierda
//
// Cuando no está en modo edición no se permite arrarstrar ni configurar ni eliminar nodos
export default Vue.extend({
  template: template(),
  components: {
    conditionConfig,
    subWorkflowConfig,
    httpRequestConfig,
    sendEmailConfig,
    splitConfig,
    ctiConfig,
    sendSmsConfig,
    startConfig,
    webhookConfig,
    updateConfig,
    sendLeadConfig,
    pauseConfig,
    agentConfig,
    waitForConfig,
    whatsappConfig
  },
  props: {
    steps: {
      type: Array,
      default: () => []
    },
    campaign: {
      type: Object,
      required: true
    },
    errors: {
      type: Object
    },
    editing: {
      type: Boolean
    },
    status: {
      type: String
    }
  },
  data() {
    return {
      nodeTypes: [
        // 'webhook',
        'start',
        'subWorkflow',
        'condition',
        'sendEmail',
        'split',
        'sendLead',
        'httpRequest',
        'update',
        'cti',
        'sendSms',
        'end',
        'pause',
        'agent',
        'waitFor',
        'whatsapp'
      ],
      nodes: [], // nodos del workflow (copia interna de steps con variables extras)
      graphItems: [], // elementos de la tabla que se pintan (nodos + caminos)
      maxX: 0, // nº de columnas del workflow
      maxY: 0, // nº de filas del workflow
      count: 0, // iterador para evitar bucles infinitos
      reducedView: false, // oculta las descripciones de los nodos
      fullscreen: false, // cierra el sidebar y el acordeón con los campos
      openedNode: null, // nodo del que se está mostrando el tooltip
      configNode: null, // nodo que se está configurando (se abre modal)
      lastId: null, // último id asignado a un nodo (se usa para añadir nuevos)
      ready: false
    }
  },
  watch: {
    errors(newVal) {
      // si llegan errores del backend, se añaden a cada nodo y se recalculan las clases
      if (newVal && newVal.steps) {
        newVal.steps.forEach((stepError, index) => {
          this.nodes[index].errors = stepError
        })

        this.calculateNodeClasses()
      }
    },

    editing(newVal) {
      // siempre que se deja de editar se vuelve a inicializar con los valores de los props
      // en el caso de cancelar la editición, el prop steps no cambia así que usamos esto
      // elimina los posibles errores existentes y deshace los cambios
      if (!newVal) {
        this.initializeNodes()
        this.recalculeGraph()
      }
    }
  },
  async created() {
    await this.loadCampaignTags()
    this.initializeNodes()
    this.recalculeGraph()
  },
  methods: {
    // como son usadas por la mayoría de nodos, las cargamos una sola vez aqui
    loadCampaignTags() {
      const params = {page: {number: 1, size: PAGINATION_ALL}}

      return API.campaigns.tags.index(this.campaign.id, params, {skipLoading: true}).then(
        response => { this.campaignTags = response.data }
      )
    },

    initializeNodes() {
      if (!this.steps?.length) {
        this.nodes.push({id: '0', stepType: 'start'})
      } else {
        this.nodes = this.steps.map(item => {
          const node = cloneDeep(item)

          // Inicializamos las variables internas
          // StepInternalKeys.forEach(key => { node[key] = null })

          return node
        })
      }
    },

    emitInfo() {
      const unfinishedNodes = this.nodes.filter(item => item.dropzone).length > 0
      const nodesWithError = this.nodes.filter(item => item.errors).length > 0

      this.$emit('info', {
        unfinishedNodes,
        nodesWithError,
        steps: this.nodes.map(step => (
          pickBy(step, key => StepInternalKeys.indexOf(key) === -1)
        ))
      })
    },

    // recalcula y dibuja de nuevo el diagrama
    // se llama cada vez que hay una modificación en los nodos
    recalculeGraph() {
      this.ready = false
      this.count = 0
      this.maxX = 0
      this.maxY = 0
      this.lastId = max(this.nodes.map(item => parseInt(item.id))) + 1
      this.graphItems = []

      this.processNode(this.nodes[0], 0, 0)
      this.calculateNodeClasses()
      this.drawBranchPathss()

      this.ready = true
      this.emitInfo()
    },

    // Función que se encarga de crear la matriz con los nodos que dibuja la tabla (diagrama)
    // - node: nodo a representar
    // - xLevel: columna en la que se tiene que colocar el nodo
    // - yLevel: fila en la que se tiene que colocar el nodo
    //
    // Devuelve la fila (Y) en la que se ha situado el nodo
    processNode(node, xLevel, yLevel) {
      this.count += 1

      // TODO: paramos un posible bucle infinito durante las pruebas
      if (this.count > 1000) {
        // eslint-disable-next-line
        throw `node: ${node.id}, x: ${xLevel}, y: ${yLevel}, count: ${this.count}`
      }

      if (MOCKS) {
        // eslint-disable-next-line
        console.log(`node: ${node?.id}, x: ${xLevel}, y: ${yLevel}, count: ${this.count}`)
      }

      if (!this.graphItems[xLevel]) this.graphItems[xLevel] = []
      if (this.maxX < xLevel) this.maxX = xLevel

      // devuelve los nodos hijos que admite o tiene este nodo
      const childs = this.getNodeChilds(node)

      // si no tiene hijos, añade el nodo en el [x,y] pasado y retorna el y
      if (!childs.length) {
        this.addNodeToGraph(node, xLevel, yLevel)
        this.maxY = yLevel

        return yLevel
      }

      // si tiene solo un hijo, procesa primero el hijo y después añade el nodo en el Y del hijo
      // esto es necesario por si el hijo tiene sub-hijos con doble camino, su fila cambia
      if (childs.length === 1) {
        const child = childs[0]

        const yChild = this.processNode(child, xLevel + 1, yLevel)
        this.addNodeToGraph(node, xLevel, yChild)

        return yChild
      }

      // si tiene 2 hijos (split o if)
      if (childs.length === 2) {
        const childA = childs[0]
        const childB = childs[1]

        // procesa primera la rama de arriba
        this.processNode(childA, xLevel + 1, yLevel)

        // después añade el nodo en un Y superior al máximo de la rama superior
        this.maxY += 1
        const currentY = this.maxY
        this.addNodeToGraph(node, xLevel, currentY)

        // y por último procesa la rama de abajo partiendo un Y superior al nodo actual
        this.processNode(childB, xLevel + 1, this.maxY + 1)

        return currentY
      }

      return null
    },

    // devuelve los nodos hijos del nodo pasado, si no existen los crea como dropzones
    getNodeChilds(node) {
      // nodo sin hijos
      if (node.stepType === 'end' || node.dropzone) return []

      // nodo con varios hijos
      if (this.isBranchNode(node)) {
        if (!node.branchs) node.branchs = []; // este ; es necesario

        [0, 1].forEach(index => {
          if (!node.branchs[index]) {
            this.lastId += 1
            const child = {dropzone: true, id: this.lastId}
            node.branchs[index] = {
              nextStepId: child.id,
              value: node.stepType === 'condition' || node.stepType === 'waitFor' ? index === 0 : 50
            }
            this.nodes.push(child)
          }
        })

        const childIds = node.branchs.map(item => item.nextStepId)

        // Importante devolver los hijos en el orden en el que están los ids
        return childIds.map(id => this.nodes.find(child => child.id === id))
      }

      // nodo con un solo hijo
      if (!node.nextStepId) {
        this.lastId += 1
        const child = {dropzone: true, id: this.lastId}
        node.nextStepId = child.id
        this.nodes.push(child)
      }

      return [this.nodes.find(item => item.id === node.nextStepId)]
    },

    addNodeToGraph(node, x, y) {
      node.x = x
      node.y = y
      this.graphItems[x][y] = node // importante que sea una referencia al nodo original
    },

    // calcula las clases a aplicar a cada nodo
    calculateNodeClasses() {
      this.nodes.forEach(node => {
        node.classes = []

        if (this.graphItems[node.x + 1] && this.graphItems[node.x + 1][node.y]) {
          node.classes.push('node--path-next')
        }

        if (node === this.openNode) node.classes.push('node--opened')
        if (node.errors) node.classes.push('node--error')
      })
    },

    // añade a la tabla los nodos que dibujan los caminos de los nodos con 2 hijos
    drawBranchPathss() {
      // recorremos todos los nodos que se bifurcan
      const branchNodes = this.nodes.filter(item => (
        ['condition', 'split', 'waitFor'].indexOf(item.stepType) > -1
      ))

     if (this.$store.getters.allNodes.lentgh !== this.nodes.length) {
          this.$store.commit('setAllNodes', [...this.nodes])
        }
      
      branchNodes.forEach(parent => {
        // buscamos sus hijos y los recorremos (serán 2)
        this.getNodeChilds(parent).forEach(child => {
          if (!child) return

          if (child.y > parent.y) { // si el hijo está por debajo en el grafo
            for (let y = parent.y + 1; y <= child.y; y++) {
              const isCorner = y === child.y

              // añadimos un nodo en el camino con la clase correspondiente
              this.graphItems[parent.x][y] = {
                type: isCorner ? 'if-false-corner' : 'if-false-path',
                class: isCorner ? 'path--bottom-corner' : 'path--branch-bottom',
                text: isCorner ? this.getBranchCornerText(parent, 1) : null
              }
            }
          } else { // si el hijo está por encima en el grafo
            for (let y = parent.y - 1; y >= child.y; y--) {
              const isCorner = y === child.y

              // añadimos un nodo en el camino con la clase correspondiente
              this.graphItems[parent.x][y] = {
                type: isCorner ? 'if-true-corner' : 'if-true-path',
                class: isCorner ? 'path--top-corner' : 'path--branch-top',
                text: isCorner ? this.getBranchCornerText(parent, 0) : null
              }
            }
          }
        })
      })
    },

    graphItem(x, y) {
      return this.graphItems[x][y] || {}
    },

    getBranchCornerText(node, branchIndex) {
      if (!node.branchs) return '-'

      const branch = node.branchs[branchIndex]
      if (node.stepType === 'condition') return branch.value ? 'TRUE' : 'FALSE'
      if (node.stepType === 'waitFor') return branch.value ? 'TRUE' : 'FALSE'
      if (node.stepType === 'split') return `${branch.value}%`

      return '-'
    },

    isBranchNode(node) {
      if (!node?.stepType) return false
      return ['condition', 'split', 'waitFor'].indexOf(node.stepType) > -1
    },

    canDeleteNode(node) {
      if (node.stepType === 'start') return false
      if (node.stepType === 'end') return true

      const childs = this.getNodeChilds(node)
      return childs.every(item => item?.dropzone)
    },

    openNode(_event, x, y) {
      // hay que ponerle una clase para subirle el z-index al nodo
      // ya que si no el bubble sale por detrás de otros nodos
      // (un elemento no puede tener un z-index mayor que el de su padre)
      this.openedNode = this.graphItems[x][y]
      this.calculateNodeClasses()
    },

    openNodeConfig(node) {
      this.calculateNodeTags() // aseguramos que están actualizadas
      this.configNode = node
      this.openedNode = null
    },

    closeNode() {
      this.calculateNodeClasses()
      this.openedNode = null
    },

    // permite calcular los tags de un nodo de forma recursiva (son los del padre)
    processNodeTags(node, parentTags = []) {
      const childs = this.getNodeChilds(node)

      childs.forEach(child => {
        // si es un nodo httpRequest sus hijos pueden usar sus variables
        if (
          (node.stepType === 'httpRequest' || node.stepType === 'agent'|| node.stepType === 'whatsapp') &&
          node.config?.responseMapping
        ) {
          const workflowTags = Object.keys(node.config.responseMapping).map(item => (
            {type: 'workflow', key: item}
          ))

          this.processNodeTags(child, parentTags.concat(workflowTags))
        } else {
          this.processNodeTags(child, parentTags)
        }
      })

      node.tags = parentTags
    },

    // calcula los tags de todos los nodos del diagrama
    calculateNodeTags() {
      this.processNodeTags(this.nodes[0], this.campaignTags)
    },

    showNoEditingAlert() {
      openModalAlert(
        this.t('noEditingAlert.title'),
        this.t('noEditingAlert.body'),
        this.t('noEditingAlert.okButton')
      )
    },

    getNodeIcon(type) {
      switch (type) {
        case 'condition': return 'pixel-control'
        case 'split': return 'zexpand-right'
        case 'webhook': return 'zsync'
        case 'httpRequest': return 'wglobe'
        case 'sendLead': return 'send'
        case 'update': return 'zsync'
        case 'cti': return 'zphone'
        case 'sendSms': return 'message'
        case 'sendEmail': return 'email'
        case 'start': return 'arrow-right'
        case 'end': return 'zstop'
        case 'pause': return 'zpause'
        case 'subWorkflow': return 'notes'
        case 'agent': return 'user'
        case 'waitFor': return 'clock'
        case 'whatsapp': return 'message'
        default: return ''
      }
    },

    drag(event, type) {
      event.dataTransfer.setData('type', type)
    },

    drop(event, graphNode) {
      const node = this.nodes.find(item => item.id === graphNode.id)
      node.stepType = event.dataTransfer.getData('type')
      delete node.dropzone
      delete node.config // hacemos que el componente de su configuración lo inicialize

      // eliminamos el nextStepId para que al eliminar se
      // pueda agregar de nuevo los nodos con un solo hijo
      delete node.nextStepId

      // lo marcamos con errores hasta que rellene la información (menos el end)
      node.errors = node.stepType !== 'end'

      this.recalculeGraph()
     
      if (node.stepType === 'waitFor') {
        if (this.$store.getters.allNodes.lentgh !== this.nodes.length) {
          this.$store.commit('setAllNodes', [...this.nodes])
        }
      }

      if (node.stepType !== 'end') {
        this.$nextTick(() => { this.openNodeConfig(node) })
      }
    },

    addNewNode(type, parent) {
      this.nodes.push({id: this.nodes.length + 1, type, parent})
    },

    removeNode(graphItem) {
      openModalConfirmRemove(
        this.t('removeNodeConfirmationModal.title'),
        this.t('removeNodeConfirmationModal.body'),
        this.t('removeNodeConfirmationModal.okButton')
      ).then(() => {
        // primero eliminamos los hijos si los tiene
        const childs = this.getNodeChilds(graphItem)
        childs.forEach(child => {
          const nodeIndex = this.nodes.findIndex(item => item.id === child.id)
          this.nodes.splice(nodeIndex, 1)
        })

        // despues, lo convertimos en un nodo dropzone
        Object.assign(graphItem, {
          dropzone: true,
          stepType: null,
          branchs: null,
          name: null,
          config: null
        })

        this.recalculeGraph()
      }).catch(() => {})
    },

    toggleFullscreen() {
      this.fullscreen = !this.fullscreen

      // cerramos o abrimos el sidebar del layour
      LayoutBus.$emit(this.fullscreen ? 'close-sidebar' : 'open-sidebar')

      // emitimos el evento para que el padre cierre / abra el acordeón del formulario
      this.$emit('fullscreen', this.fullscreen)
    },

    updateNode(info) {
      Object.assign(this.configNode, info) // para que funcione el binding sin recalcular
      this.configNode = null
      this.calculateNodeClasses()
      this.recalculeGraph()
      this.emitInfo()
    },

    nodeHasConfig(node) {
      return ['end'].indexOf(node.stepType) === -1
    },

    t(key, options = {}) {
      return this.$t(`campaigns.show.automations.workflow.${key}`, options)
    }
  }
})
